/**
 * FinishSignInGate — holds the signed-in app until a passkey account has a way
 * back in. A passkey sign-up, or an invite accepted with a passkey, signs in
 * with a password the device generated (utilities/pendingCredential); the
 * person must then save the passkey or set a password of their own. Mounted
 * with the other global overlays in RootNavigator; renders nothing otherwise.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Modal, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import { useTheme } from '../hooks/useTheme';
import Icon from './Icon';
import { isPasskeySupported, registerPasskey } from '../utilities/passkeys';
import { cognitoChangePassword, cognitoForgotPassword, cognitoConfirmForgotPassword } from '../utilities/cognitoAuth';
import { getAccessToken } from '../utilities/tokenStore';
import { clearPendingCredential, getPendingCredential, type PendingCredential } from '../utilities/pendingCredential';

const POLICY = 'Choose a password with at least 8 characters, including an uppercase letter, a lowercase letter, and a number.';

export default function FinishSignInGate() {
  const theme = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const email = useSelector((state: RootState) => state.session.last_logged_in?.email as string | undefined);
  const [pending, setPending] = useState<PendingCredential | null>(null);
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [resetRequested, setResetRequested] = useState(false);
  const [busy, setBusy] = useState<'passkey' | 'password' | null>(null);
  const [error, setError] = useState('');
  const passkeys = isPasskeySupported();

  useEffect(() => {
    let live = true;
    if (email) getPendingCredential(email).then((found) => { if (live) setPending(found); });
    else setPending(null);
    return () => { live = false; };
  }, [email]);

  if (!pending) return null;

  const done = () => clearPendingCredential().then(() => setPending(null));

  const savePasskey = () => {
    setBusy('passkey');
    setError('');
    registerPasskey()
      .then((result) => {
        if (result.success) return done();
        // Backing out of the system sheet is a choice, not an error.
        if (!/cancel/i.test(result.message || '')) setError(result.message || 'The passkey was not saved. Try again.');
        return undefined;
      })
      .finally(() => setBusy(null));
  };

  const needsRecovery = !pending.password;
  const passwordButtonLabel = needsRecovery && !resetRequested ? 'Send reset code' : 'Continue with a password';

  const savePassword = () => {
    if (busy) return;
    if (needsRecovery && !resetRequested) {
      setBusy('password');
      setError('');
      cognitoForgotPassword(pending.email)
        .then(() => setResetRequested(true))
        .catch(() => setError('Could not send a reset code. Try again.'))
        .finally(() => setBusy(null));
      return;
    }
    if (needsRecovery && !resetCode.trim()) {
      setError('Enter your reset code.');
      return;
    }
    if (password.length < 8) {
      setError(POLICY);
      return;
    }
    setBusy('password');
    setError('');
    const save = needsRecovery
      ? cognitoConfirmForgotPassword(pending.email, resetCode.trim(), password)
      : getAccessToken().then((token) => cognitoChangePassword(token, pending.password!, password));
    save.then(done)
      .catch((err: any) => {
        setError(/InvalidPassword|InvalidParameter/.test(String(err?.code || err?.name || err?.message)) ? POLICY : 'That did not work. Try again.');
      })
      .finally(() => setBusy(null));
  };

  return (
    <Modal visible animationType="fade" onRequestClose={() => {}}>
      <ScrollView
        testID="finish-sign-in"
        style={{ backgroundColor: theme.colors.background }}
        contentContainerStyle={styles.scroll}
        keyboardShouldPersistTaps="handled"
      >
        <View style={[styles.card, { backgroundColor: theme.colors.card, borderColor: theme.colors.border }]}>
          <Text style={[styles.title, { color: theme.colors.text }]}>One more step</Text>
          <Text style={[styles.subtitle, { color: theme.colors.muted }]}>Choose how you&apos;ll sign in next time.</Text>

          {passkeys ? (
            <>
              <TouchableOpacity
                style={[styles.button, { backgroundColor: theme.colors.primary }]}
                onPress={savePasskey}
                disabled={!!busy}
                activeOpacity={0.85}
                accessibilityRole="button"
                accessibilityLabel="Save a passkey"
              >
                {busy === 'passkey'
                  ? <ActivityIndicator color="#fff" size="small" />
                  : (
                    <View style={styles.inner}>
                      <Icon icon={['fad', 'fingerprint']} size={18} color="#fff" />
                      <Text style={styles.buttonText}>Save a passkey</Text>
                    </View>
                  )}
              </TouchableOpacity>
              <Text style={[styles.hint, { color: theme.colors.muted }]}>Face ID, fingerprint, or your device PIN. Nothing to remember.</Text>
              <View style={styles.orDivider}>
                <View style={[styles.orLine, { backgroundColor: theme.colors.border }]} />
                <Text style={[styles.orText, { color: theme.colors.muted }]}>or create a password</Text>
                <View style={[styles.orLine, { backgroundColor: theme.colors.border }]} />
              </View>
            </>
          ) : null}

          {needsRecovery ? <Text style={[styles.hint, { color: theme.colors.muted }]}>
            {resetRequested
              ? 'Enter the code sent to your verified email or phone, then choose a password.'
              : 'This page was reopened before sign-in setup finished. Save a passkey, or request a code to choose a password.'}
          </Text> : null}
          {needsRecovery && resetRequested ? (
            <TextInput
              style={[styles.input, { borderColor: theme.colors.border, color: theme.colors.text, backgroundColor: theme.colors.surface }]}
              value={resetCode}
              onChangeText={setResetCode}
              placeholder="Reset code"
              accessibilityLabel="Reset code"
              autoComplete="one-time-code"
              textContentType="oneTimeCode"
              keyboardType="number-pad"
            />
          ) : null}
          {(!needsRecovery || resetRequested) ? <>
          <Text style={[styles.label, { color: theme.colors.muted }]}>Password</Text>
          <View>
            <TextInput
              style={[styles.input, { borderColor: theme.colors.border, color: theme.colors.text, backgroundColor: theme.colors.surface }]}
              value={password}
              onChangeText={(next) => { setPassword(next); setError(''); }}
              placeholder="Min. 8 characters"
              placeholderTextColor={theme.colors.muted}
              secureTextEntry={!showPw}
              autoComplete="new-password"
              textContentType="newPassword"
              maxLength={32}
              returnKeyType="go"
              onSubmitEditing={savePassword}
            />
            <TouchableOpacity style={styles.eyeBtn} onPress={() => setShowPw(!showPw)} accessibilityRole="button" accessibilityLabel={showPw ? 'Hide password' : 'Show password'}>
              <Text style={{ color: theme.colors.muted, fontSize: 12 }}>{showPw ? 'Hide' : 'Show'}</Text>
            </TouchableOpacity>
          </View>
          </> : null}
          {error ? <Text style={styles.errorText}>{error}</Text> : null}
          <TouchableOpacity
            style={[styles.button, styles.spaced, passkeys
              ? [styles.outlined, { borderColor: theme.colors.border, backgroundColor: theme.colors.surface }]
              : { backgroundColor: theme.colors.primary }]}
            onPress={savePassword}
            disabled={!!busy}
            activeOpacity={0.85}
            accessibilityRole="button"
          >
            {busy === 'password'
              ? <ActivityIndicator color={passkeys ? theme.colors.primary : '#fff'} size="small" />
              : <Text style={[styles.buttonText, passkeys && { color: theme.colors.text }]}>{passwordButtonLabel}</Text>}
          </TouchableOpacity>
        </View>
      </ScrollView>
    </Modal>
  );
}

const makeStyles = (theme: ReturnType<typeof useTheme>) =>
  StyleSheet.create({
    scroll: { flexGrow: 1, justifyContent: 'center', paddingHorizontal: 28, paddingVertical: 40 },
    card: {
      borderRadius: 20,
      borderWidth: StyleSheet.hairlineWidth,
      padding: 26,
      width: '100%',
      maxWidth: 480,
      alignSelf: 'center',
    },
    title: { fontSize: 22, fontWeight: '700', textAlign: 'center', marginBottom: 6 },
    subtitle: { fontSize: 14, lineHeight: 20, textAlign: 'center', marginBottom: 22 },
    label: { fontSize: 13, fontWeight: '500', marginBottom: 5 },
    input: { borderWidth: 1, borderRadius: 10, paddingHorizontal: 14, paddingVertical: 11, paddingRight: 56, fontSize: 15 },
    eyeBtn: { position: 'absolute', right: 14, top: 0, bottom: 0, justifyContent: 'center' },
    button: { height: 50, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
    spaced: { marginTop: 14 },
    outlined: { borderWidth: 1 },
    inner: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    buttonText: { color: '#fff', fontSize: 15, fontWeight: '600' },
    hint: { fontSize: 12, lineHeight: 16, marginTop: 8, textAlign: 'center' },
    orDivider: { flexDirection: 'row', alignItems: 'center', marginVertical: 18, gap: 10 },
    orLine: { flex: 1, height: StyleSheet.hairlineWidth },
    orText: { fontSize: 12, fontWeight: '500' },
    errorText: { color: theme.colors.error, fontSize: 13, marginTop: 8 },
  });
